import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { CareConciergeFormData, RelationshipType, RequestorType, UrgencyLevel } from './types';
import { formatUSPhone } from '@/shared/utils/formatters';

type CareSeekerWorkflowProps = {
  register: UseFormRegister<CareConciergeFormData>;
  errors: FieldErrors<CareConciergeFormData>;
  urgencyLevel?: UrgencyLevel;
  relationshipToRecipient?: RelationshipType;
  needsSameLocation?: boolean;
  requestorType?: RequestorType;
};

const CareSeekerWorkflow: React.FC<CareSeekerWorkflowProps> = ({
  register,
  errors,
  urgencyLevel,
  relationshipToRecipient,
  needsSameLocation,
  requestorType,
}) => {
  const showDatePicker = urgencyLevel === 'IMMEDIATE_CRISIS' || urgencyLevel === 'CRISIS';
  const showRelationshipDesc =
    relationshipToRecipient === 'OTHER_RELATIVE' || relationshipToRecipient === 'OTHER';
  const showAlternateLocation = needsSameLocation === false;
  const showCareRecipientName = requestorType === 'LOVED_ONE';

  return (
    <div className="space-y-8">
      {/* Contact Information */}
      <div>
        <h4 className="text-xl font-recoleta font-semibold text-[#111827] mb-4">
          Your Contact Information
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Your Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('requestorName', { required: 'Name is required' })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            />
            {errors.requestorName && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.requestorName.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              {...register('requestorEmail', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            />
            {errors.requestorEmail && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.requestorEmail.message}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              placeholder="(555) 123-4567"
              {...register('requestorPhone', {
                required: 'Phone number is required',
                pattern: {
                  value: /^\(\d{3}\) \d{3}-\d{4}$/,
                  message: 'Enter a valid US phone number',
                },
                onChange: (e) => {
                  e.target.value = formatUSPhone(e.target.value);
                },
              })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            />
            {errors.requestorPhone && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.requestorPhone.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Care Recipient Details */}
      <div>
        <h4 className="text-xl font-recoleta font-semibold text-[#111827] mb-4">
          About the Person Needing Support
        </h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Relationship to you <span className="text-red-500">*</span>
            </label>
            <select
              {...register('relationshipToRecipient', { required: 'Please select a relationship' })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            >
              <option value="">Select relationship...</option>
              <option value="MYSELF">Myself</option>
              <option value="PARENT">Parent</option>
              <option value="SPOUSE_PARTNER">Spouse/Partner</option>
              <option value="OTHER_RELATIVE">Other Relative</option>
              <option value="OTHER">Other</option>
            </select>
            {errors.relationshipToRecipient && (
              <p className="mt-1 text-sm text-red-600 font-termina">
                {errors.relationshipToRecipient.message}
              </p>
            )}
          </div>

          {showRelationshipDesc && (
            <div>
              <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
                Please describe the relationship <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('relationshipOtherDesc', {
                  required: showRelationshipDesc ? 'Please describe the relationship' : false,
                  maxLength: {
                    value: 200,
                    message: 'Description must be 200 characters or less',
                  },
                })}
                className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
                maxLength={200}
              />
              {errors.relationshipOtherDesc && (
                <p className="mt-1 text-sm text-red-600 font-termina">
                  {errors.relationshipOtherDesc.message}
                </p>
              )}
            </div>
          )}

          {showCareRecipientName && (
            <div>
              <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
                Care recipient's name
              </label>
              <input
                type="text"
                {...register('careRecipientName', {
                  maxLength: { value: 200, message: 'Name must be 200 characters or less' },
                })}
                className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
                maxLength={200}
                placeholder="Name of the person needing support"
              />
              {errors.careRecipientName && (
                <p className="mt-1 text-sm text-red-600 font-termina">
                  {errors.careRecipientName.message}
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Brief description of the situation <span className="text-red-500">*</span>
            </label>
            <textarea
              {...register('situationDescription', {
                required: 'Please describe the situation',
                maxLength: {
                  value: 500,
                  message: 'Description must be 500 characters or less',
                },
              })}
              rows={4}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm resize-none"
              maxLength={500}
              placeholder="Tell us briefly about the type of help needed..."
            />
            {errors.situationDescription && (
              <p className="mt-1 text-sm text-red-600 font-termina">
                {errors.situationDescription.message}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Urgency Assessment */}
      <div>
        <h4 className="text-xl font-recoleta font-semibold text-[#111827] mb-4">Urgency</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              How urgent is this need? <span className="text-red-500">*</span>
            </label>
            <select
              {...register('urgencyLevel', { required: 'Please select urgency level' })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            >
              <option value="">Select urgency...</option>
              <option value="IMMEDIATE_CRISIS">Immediate crisis - support needed now</option>
              <option value="WITHIN_WEEK">Within the next week</option>
              <option value="WITHIN_MONTH">Within the next month</option>
              <option value="PLANNING_AHEAD">Planning ahead</option>
            </select>
            {errors.urgencyLevel && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.urgencyLevel.message}</p>
            )}
          </div>

          {showDatePicker && (
            <div>
              <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
                Service needed by date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                {...register('serviceNeededByDate', {
                  required: showDatePicker ? 'Please provide a date for crisis urgency' : false,
                })}
                className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
                min={new Date().toISOString().split('T')[0]}
              />
              {errors.serviceNeededByDate && (
                <p className="mt-1 text-sm text-red-600 font-termina">
                  {errors.serviceNeededByDate.message}
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Is the person currently safe? <span className="text-red-500">*</span>
            </label>
            <select
              {...register('safetyStatus', { required: 'Please indicate safety status' })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            >
              <option value="">Select...</option>
              <option value="YES">Yes</option>
              <option value="NO">No</option>
              <option value="NOT_SURE">Not sure</option>
            </select>
            {errors.safetyStatus && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.safetyStatus.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Any recent triggering event? (Optional)
            </label>
            <textarea
              {...register('triggeringEvent', {
                maxLength: {
                  value: 500,
                  message: 'Description must be 500 characters or less',
                },
              })}
              rows={3}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm resize-none"
              maxLength={500}
              placeholder="E.g., recent fall, hospitalization, etc."
            />
            {errors.triggeringEvent && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.triggeringEvent.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Location */}
      <div>
        <h4 className="text-xl font-recoleta font-semibold text-[#111827] mb-4">Location</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Where does the person needing support currently live? <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('currentLocation', { required: 'Current location is required' })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
              placeholder="City, State, ZIP or full address"
            />
            {errors.currentLocation && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.currentLocation.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Is support needed in this same location? <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  value="true"
                  {...register('needsSameLocation', {
                    required: 'Please indicate if support is needed in the same location',
                  })}
                  className="w-4 h-4 text-[#026857] border-[#E5E7EB] focus:ring-[#026857]"
                />
                <span className="font-termina text-sm text-[#111827]">Yes</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  value="false"
                  {...register('needsSameLocation', {
                    required: 'Please indicate if support is needed in the same location',
                  })}
                  className="w-4 h-4 text-[#026857] border-[#E5E7EB] focus:ring-[#026857]"
                />
                <span className="font-termina text-sm text-[#111827]">No</span>
              </label>
            </div>
            {errors.needsSameLocation && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.needsSameLocation.message}</p>
            )}
          </div>

          {showAlternateLocation && (
            <div>
              <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
                Where is support needed? <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('alternateLocation', {
                  required: showAlternateLocation ? 'Alternate location is required' : false,
                })}
                className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
                placeholder="City, State, ZIP or full address"
              />
              {errors.alternateLocation && (
                <p className="mt-1 text-sm text-red-600 font-termina">
                  {errors.alternateLocation.message}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Preferences */}
      <div>
        <h4 className="text-xl font-recoleta font-semibold text-[#111827] mb-4">Your Preferences</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              How would you like to receive recommendations? <span className="text-red-500">*</span>
            </label>
            <select
              {...register('recommendationMethod', { required: 'Please select a recommendation method' })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            >
              <option value="">Select method...</option>
              <option value="DIRECT_CONTACT">
                Direct contact - bQuest will connect me directly with providers
              </option>
              <option value="PROVIDE_LIST">Provide a list - I'll reach out myself</option>
            </select>
            {errors.recommendationMethod && (
              <p className="mt-1 text-sm text-red-600 font-termina">
                {errors.recommendationMethod.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Preferred method of communication <span className="text-red-500">*</span>
            </label>
            <select
              {...register('preferredCommunication', {
                required: 'Please select a communication preference',
              })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
            >
              <option value="">Select method...</option>
              <option value="PHONE">Phone</option>
              <option value="ZOOM">Zoom/Video call</option>
              <option value="EMAIL">Email</option>
            </select>
            {errors.preferredCommunication && (
              <p className="mt-1 text-sm text-red-600 font-termina">
                {errors.preferredCommunication.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Financial firm affiliation (Optional)
            </label>
            <input
              type="text"
              {...register('financialFirmAffiliation')}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
              placeholder="Your financial advisor's firm"
            />
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Financial advisor name (Optional)
            </label>
            <input
              type="text"
              {...register('financialAdvisorName')}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
              placeholder="Your financial advisor's name"
            />
          </div>

          <div>
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                {...register('loopInFinancialAdvisor')}
                className="mt-1 w-4 h-4 text-[#026857] border-[#E5E7EB] rounded focus:ring-[#026857]"
              />
              <span className="text-sm font-termina text-[#111827]">
                Loop in my financial advisor on recommendations
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Additional Details */}
      <div>
        <h4 className="text-xl font-recoleta font-semibold text-[#111827] mb-4">
          Additional Details
        </h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Categories of interest
            </label>
            <input
              type="text"
              {...register('categories')}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
              placeholder="e.g. Home Care, Memory Care (comma-separated)"
            />
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              Special needs or requirements
            </label>
            <input
              type="text"
              {...register('specialNeeds', {
                maxLength: { value: 500, message: 'Special needs must be 500 characters or less' },
              })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
              maxLength={500}
              placeholder="e.g. Wheelchair accessibility, dietary restrictions"
            />
            {errors.specialNeeds && (
              <p className="mt-1 text-sm text-red-600 font-termina">{errors.specialNeeds.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-termina font-medium text-[#111827] mb-1">
              When is care needed?
            </label>
            <input
              type="date"
              {...register('careStartDate')}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#026857] focus:border-transparent font-termina text-sm"
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareSeekerWorkflow;
